/**
 * Distance attenuation and stereo panning, from
 * `doc/grind/architecture/AUDIO-VISUAL-EFFECTS.md` §1.
 *
 * Pure and Babylon-free, so the curve can be tested without a GL context or an
 * `AudioContext` — the same reason `game/lerp.ts` and `models/poses.ts` exist.
 * The mixer applies the numbers; this decides them.
 */

/** Inside this radius a sound plays at full volume. */
export const MIN_DISTANCE = 4;

/**
 * Beyond this, silence.
 *
 * The spec's 30 m matches roughly what the gameplay camera shows, which is the
 * point: a sound you cannot see the source of should be quiet, and one from the
 * far side of the map should not play at all.
 */
export const MAX_DISTANCE = 30;

/** How sharply volume falls between the two. */
export const ROLLOFF = 1.5;

/**
 * Volume multiplier for a sound `distance` units from the listener.
 *
 * The spec's curve, `1 / (1 + rolloff × (d - dmin) / (dmax - dmin))`, with two
 * clamps it does not write down but does require:
 *
 *  - **Below `dmin`, full volume.** Otherwise the formula keeps climbing above 1
 *    as a sound approaches, and a hit landing on the player would be louder than
 *    the mixer's headroom allows.
 *  - **Beyond `dmax`, zero.** The bare formula bottoms out at 0.4, so a monster
 *    across the map would still be audible at nearly half volume; the spec says
 *    "sounds further than 30m off-screen are muted", so this returns silence
 *    rather than a floor.
 */
export function attenuation(
  distance: number,
  min = MIN_DISTANCE,
  max = MAX_DISTANCE,
  rolloff = ROLLOFF,
): number {
  if (!Number.isFinite(distance) || distance <= min) return 1;
  if (distance >= max) return 0;
  return 1 / (1 + rolloff * ((distance - min) / (max - min)));
}

/**
 * Stereo position for a sound `dx` world units to the listener's right.
 *
 * -1 is hard left, +1 hard right. Linear rather than curved, and saturating at
 * the same radius the volume does: the job is to say *which side*, so a monster
 * about to walk into view is heard coming.
 *
 * Only the X axis matters. The camera is fixed isometric, so world X is always
 * screen-horizontal — if the camera ever rotates, this needs its yaw.
 */
export function stereoPan(dx: number, max = MAX_DISTANCE): number {
  if (!Number.isFinite(dx) || max <= 0) return 0;
  return Math.max(-1, Math.min(1, dx / max));
}

/** Distance between two points on the ground plane. */
export function distanceBetween(
  a: { x: number; z: number },
  b: { x: number; z: number },
): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}
